'use client';

import { useState } from 'react';

import Link from 'next/link';

import { ArrowRight, Check } from 'lucide-react';

import type { BillingConfig } from '@kit/billing';
import { Trans } from '@kit/ui/trans';

import { useWebsiteBeta } from '~/lib/use-website-beta';

import { useCurrency } from '../../../_components/currency-selector';
import pearl from '../../_components/website-pearl-button.module.css';
import styles from '../pricing.module.css';
import { PricingAddons } from './pricing-addons';
import { PricingMcpPlans } from './pricing-mcp-plans';
import {
  pricingFeatureLabel,
  pricingFeatures,
  pricingSupport,
} from './pricing-presentation';

export function PricingPageClient({
  paths: _paths,
  configs,
  initialCurrency = 'USD',
  preview = false,
}: {
  paths: { signUp: string; return: string };
  configs: Record<'USD' | 'AUD', BillingConfig>;
  initialCurrency?: 'USD' | 'AUD';
  preview?: boolean;
}) {
  const betaEnquiryEnabled = useWebsiteBeta();
  const { currency: detected, setCurrency } = useCurrency(initialCurrency);
  const currency = detected === 'AUD' ? 'AUD' : 'USD';
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const money = (amount: number) =>
    new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 ? 2 : 0,
    }).format(amount);
  return (
    <div className={styles.plans}>
      {betaEnquiryEnabled && (
        <section
          className={styles.betaProgram}
          aria-labelledby="beta-program-title"
        >
          <div>
            <span className={styles.betaEyebrow}>
              Free beta · until 14 December 2026
            </span>
            <h2 id="beta-program-title">Help shape what comes next.</h2>
            <p>
              We’re inviting you to join Leagen’s launch beta and help us
              fine-tune the experience. Try it with your team and tell us what
              would make it better.
            </p>
            <p>
              Every Pro feature is free until 14 December 2026, with 12,500 monthly credits and up to 20 seats. After the beta,
              choose the free plan or a paid plan to continue. Explore the plans
              below.
            </p>
          </div>
          <Link
            href={_paths.signUp}
            aria-disabled={preview || undefined}
            tabIndex={preview ? -1 : undefined}
            onClick={preview ? (event) => event.preventDefault() : undefined}
            className={`${styles.cta} ${pearl.pearl}`}
          >
            Request beta access <ArrowRight size={17} />
          </Link>
        </section>
      )}
      <div className={styles.controls}>
        <div
          className={styles.switch}
          role="group"
          aria-label="Billing interval"
        >
          <button
            type="button"
            aria-pressed={interval === 'month'}
            onClick={() => setInterval('month')}
          >
            <Trans i18nKey="marketing:pricingPage.monthly" />
          </button>
          <button
            type="button"
            aria-pressed={interval === 'year'}
            onClick={() => setInterval('year')}
          >
            <Trans i18nKey="marketing:pricingPage.yearly" />{' '}
            <span>
              <Trans i18nKey="marketing:pricingPage.saving" />
            </span>
          </button>
        </div>
        <div
          className={styles.switch}
          role="group"
          aria-label="Pricing currency"
        >
          {(['USD', 'AUD'] as const).map((code) => (
            <button
              key={code}
              type="button"
              aria-pressed={currency === code}
              onClick={() => {
                setCurrency(code);
                try {
                  localStorage.setItem('leagen-currency-preference', code);
                } catch {
                  /* Currency still works without storage. */
                }
              }}
            >
              {code}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.iceSurface}>
        <div className={styles.grid}>
          {configs[currency].products.map((product, index) => {
            const previous = configs[currency].products[index - 1];
            const allFeatures = pricingFeatures(product.id, product.features);
            const priorFeatures = previous
              ? pricingFeatures(previous.id, previous.features)
              : [];
            const features = allFeatures.filter(
              (feature) => !priorFeatures.includes(feature),
            );
            const free = product.id === 'free';
            const plan =
              product.plans.find((p) => p.interval === interval) ??
              product.plans[0];
            if (!plan) return null;
            const total = plan.lineItems
              .filter((item) => item.type === 'flat')
              .reduce((sum, item) => sum + item.cost, 0);
            const monthly = interval === 'year' && !free ? total / 12 : total;
            return (
              <article
                key={product.id}
                className={styles.card}
                data-highlighted={!!product.highlighted}
              >
                <div className={styles.cardHeading}>
                  <h2>{product.name}</h2>
                  {product.highlighted && (
                    <span>
                      <Trans i18nKey="marketing:pricingPage.popular" />
                    </span>
                  )}
                </div>
                <p className={styles.price}>
                  {money(monthly)}
                  <span>
                    / <Trans i18nKey="marketing:pricingPage.month" />
                  </span>
                </p>
                <p className={styles.description}>{product.description}</p>
                <p className={styles.billing}>
                  {free ? (
                    <Trans i18nKey="marketing:pricingPage.freeForever" />
                  ) : interval === 'year' ? (
                    <Trans
                      i18nKey="marketing:pricingPage.billedYearly"
                      values={{ amount: money(total), currency }}
                    />
                  ) : (
                    <Trans i18nKey="marketing:pricingPage.billedMonthly" />
                  )}
                </p>

                <p className={styles.inheritance}>
                  {previous ? (
                    <>
                      Everything in <b>{previous.name}</b>, plus
                    </>
                  ) : (
                    'Your starting point'
                  )}
                </p>
                <ul>
                  {[...features, pricingSupport(product.id)].map((feature) => (
                    <li key={feature}>
                      <Check size={16} aria-hidden="true" />
                      <span>{pricingFeatureLabel(feature)}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
        <p className={styles.limitsNote}>
          Upgraded limits replace the previous plan’s limits.
        </p>
      </div>
      <PricingMcpPlans currency={currency} />
      <PricingAddons currency={currency} />
    </div>
  );
}
